(() => {
  const SELECTOR = '.pantheon-cro-product, .pantheon-cro-bundle';
  const STYLE_ID = 'pantheon-bundle-savings-style';

  function formatMoney(cents, currency) {
    const amount = Number(cents || 0) / 100;

    try {
      return new Intl.NumberFormat('en-GB', {
        style: 'currency',
        currency: currency || 'GBP',
      }).format(amount);
    } catch (error) {
      return `£${amount.toFixed(2)}`;
    }
  }

  function getProductHandle(card) {
    const link = card.querySelector('a[href*="/products/"]');
    if (!link) return '';

    try {
      const url = new URL(link.getAttribute('href'), window.location.origin);
      return url.pathname.split('/products/')[1]?.split('/')[0] || '';
    } catch (error) {
      return '';
    }
  }

  function getVariant(product) {
    return product?.variants?.find((variant) => variant.available) || product?.variants?.[0] || product;
  }

  function getPriceData(product) {
    const variant = getVariant(product);
    const price = Number(variant?.price || product?.price || product?.price_min || 0);
    const compareAt = Number(
      variant?.compare_at_price || product?.compare_at_price || product?.compare_at_price_max || 0
    );

    if (!price || !compareAt || compareAt <= price) return null;

    return {
      price,
      compareAt,
      percent: Math.round(((compareAt - price) / compareAt) * 100),
    };
  }

  async function fetchProduct(handle) {
    const response = await fetch(`/products/${encodeURIComponent(handle)}.js`, {
      headers: { Accept: 'application/json' },
    });

    if (!response.ok) return null;
    return response.json();
  }

  function injectStyles() {
    if (document.getElementById(STYLE_ID)) return;

    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = `
      .pantheon-cro-product .pantheon-bundle-savings,
      .pantheon-cro-bundle .pantheon-bundle-savings {
        display: flex;
        flex-wrap: wrap;
        align-items: baseline;
        gap: 0.45rem 0.65rem;
        margin-block: 0.1rem 0.35rem;
      }

      ${SELECTOR} {
        position: relative;
      }

      .pantheon-cro-product .pantheon-bundle-savings__price,
      .pantheon-cro-bundle .pantheon-bundle-savings__price {
        color: rgb(255 255 255);
        font-weight: 800;
      }

      .pantheon-cro-product .pantheon-bundle-savings__compare,
      .pantheon-cro-bundle .pantheon-bundle-savings__compare {
        color: rgb(255 255 255 / 0.56);
        font-weight: 700;
        text-decoration: line-through;
        text-decoration-thickness: 0.08em;
      }

      .pantheon-cro-product .pantheon-bundle-savings__label,
      .pantheon-cro-product .pantheon-bundle-savings-badge,
      .pantheon-cro-bundle .pantheon-bundle-savings__label,
      .pantheon-cro-bundle .pantheon-bundle-savings-badge {
        display: inline-flex;
        width: fit-content;
        align-items: center;
        justify-content: center;
        border: 1px solid rgb(214 190 111 / 0.6);
        background: rgb(214 190 111 / 0.12);
        color: rgb(242 218 127);
        font-size: 0.78rem;
        font-weight: 800;
        line-height: 1;
        text-transform: uppercase;
      }

      .pantheon-cro-product .pantheon-bundle-savings__label,
      .pantheon-cro-bundle .pantheon-bundle-savings__label {
        padding: 0.34rem 0.5rem;
      }

      .pantheon-cro-product .pantheon-bundle-savings-badge,
      .pantheon-cro-bundle .pantheon-bundle-savings-badge {
        z-index: 2;
        padding: 0.45rem 0.62rem;
      }

      .pantheon-cro-bundle .pantheon-bundle-savings-badge {
        position: absolute;
        top: 14px;
        left: 14px;
      }

      .pantheon-cro-product .pantheon-bundle-savings-badge {
        min-height: 28px;
        padding: 5px 9px;
        background: rgb(214 190 111 / 0.18);
      }

      .pantheon-cro-product .pantheon-bundle-savings__legacy-price,
      .pantheon-cro-bundle .pantheon-bundle-savings__legacy-price {
        display: none !important;
      }
    `;

    document.head.append(style);
  }

  function findInsertTarget(card) {
    return (
      card.querySelector('.pantheon-cro-product__body') ||
      card.querySelector('.pantheon-cro-bundle__footer') ||
      card.querySelector('.pantheon-cro-bundle__body') ||
      card
    );
  }

  function hideLegacyPrice(card, priceText) {
    const candidates = Array.from(card.querySelectorAll('p, span, strong, div')).filter((element) => {
      if (element.closest('.pantheon-bundle-savings')) return false;
      if (element.classList.contains('pantheon-bundle-savings-badge')) return false;
      if (element.classList.contains('pantheon-cro-product__badges')) return false;
      if (element.matches('a, button')) return false;

      const text = element.textContent.trim();
      return text === priceText || text.replace(/\s+/g, '') === priceText.replace(/\s+/g, '');
    });

    candidates[0]?.classList.add('pantheon-bundle-savings__legacy-price');
  }

  function updateBadge(card, percent) {
    const label = `Save ${percent}%`;
    const badge = card.querySelector('.pantheon-bundle-savings-badge');

    if (badge) {
      badge.textContent = label;
      return;
    }

    const createdBadge = document.createElement('span');
    createdBadge.className = 'pantheon-bundle-savings-badge';
    createdBadge.textContent = label;

    const productBadges = card.querySelector('.pantheon-cro-product__badges');
    if (productBadges) {
      productBadges.prepend(createdBadge);
      return;
    }

    const bundleImage = card.querySelector('.pantheon-cro-bundle__image');
    const bundleBadge = bundleImage?.querySelector(':scope > span');
    if (bundleBadge) {
      bundleBadge.classList.add('pantheon-bundle-savings-badge');
      bundleBadge.textContent = label;
      return;
    }

    card.prepend(createdBadge);
  }

  function renderSavings(card, product, priceData) {
    const currency = window.Shopify?.currency?.active || product?.currency || 'GBP';
    const priceText = formatMoney(priceData.price, currency);
    const compareText = formatMoney(priceData.compareAt, currency);

    hideLegacyPrice(card, priceText);
    updateBadge(card, priceData.percent);

    let row = card.querySelector('.pantheon-bundle-savings');
    if (!row) {
      row = document.createElement('div');
      row.className = 'pantheon-bundle-savings';
    }

    row.innerHTML = `
      <span class="pantheon-bundle-savings__price">${priceText}</span>
      <s class="pantheon-bundle-savings__compare">${compareText}</s>
      <span class="pantheon-bundle-savings__label">Save ${priceData.percent}%</span>
    `;

    const target = findInsertTarget(card);
    const action = target.querySelector('form, a.button, button.button');

    if (action) {
      target.insertBefore(row, action);
    } else {
      target.append(row);
    }
  }

  async function enhanceCard(card) {
    if (card.dataset.homeSavingsReady === 'true') return;
    card.dataset.homeSavingsReady = 'true';

    const handle = getProductHandle(card);
    if (!handle) return;

    try {
      const product = await fetchProduct(handle);
      const priceData = getPriceData(product);
      if (!product || !priceData) return;

      renderSavings(card, product, priceData);
    } catch (error) {
      card.dataset.homeSavingsReady = 'false';
    }
  }

  function enhanceHomepageSavings() {
    injectStyles();
    document.querySelectorAll(SELECTOR).forEach(enhanceCard);
  }

  document.addEventListener('DOMContentLoaded', enhanceHomepageSavings);
  document.addEventListener('shopify:section:load', enhanceHomepageSavings);
  window.setTimeout(enhanceHomepageSavings, 800);
})();
